import React from "react";

type MessagesSkeletonProps = {
  variant?: "mobile" | "desktop";
};

type BubbleConfig = {
  align: "left" | "right";
  widths: string[];
};

const MOBILE_BUBBLES: BubbleConfig[] = [
  { align: "left", widths: ["w-[62%]", "w-[48%]"] },
  { align: "right", widths: ["w-[54%]", "w-[38%]"] },
  { align: "left", widths: ["w-[70%]", "w-[44%]"] },
  { align: "right", widths: ["w-[58%]"] },
];

const DESKTOP_BUBBLES: BubbleConfig[] = [
  { align: "left", widths: ["w-[85%]", "w-[60%]"] },
  { align: "right", widths: ["w-[75%]", "w-[50%]"] },
  { align: "left", widths: ["w-[90%]"] },
  { align: "right", widths: ["w-[65%]", "w-[45%]"] },
];

const MessageBubbleSkeleton: React.FC<{
  align: "left" | "right";
  widths: string[];
  variant: "mobile" | "desktop";
}> = ({ align, widths, variant }) => {
  const isMobile = variant === "mobile";
  const isReceived = align === "left";

  return (
    <div className={`flex w-full ${isReceived ? "justify-start" : "justify-end"}`}>
      <div className="flex items-end gap-2 max-w-[85%] animate-pulse">
        <div
          className={`rounded-full bg-gray-200/80 flex-shrink-0 ${
            isMobile ? "h-9 w-9 sm:h-10 sm:w-10" : "h-8 w-8"
          }`}
        />
        <div
          className={`rounded-2xl px-3 py-2.5 space-y-2 ${
            isReceived
              ? "bg-white border border-gray-200"
              : "bg-[#E9E9E9]/80"
          } ${isMobile ? "min-w-[140px]" : "min-w-[120px]"}`}
        >
          {widths.map((width, index) => (
            <div
              key={index}
              className={`h-2.5 sm:h-3 rounded-full bg-gray-200/80 ${width}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const MessagesSkeleton: React.FC<MessagesSkeletonProps> = ({ variant = "mobile" }) => {
  const bubbles = variant === "mobile" ? MOBILE_BUBBLES : DESKTOP_BUBBLES;
  const isMobile = variant === "mobile";

  return (
    <div
      role="status"
      aria-label="Loading messages"
      className={
        isMobile
          ? "flex flex-col gap-2 sm:gap-3 min-h-full justify-end w-full"
          : "flex flex-col justify-end h-full w-full py-2"
      }
    >
      {bubbles.map((bubble, index) => (
        <MessageBubbleSkeleton
          key={index}
          align={bubble.align}
          widths={bubble.widths}
          variant={variant}
        />
      ))}
      <span className="sr-only">Loading messages...</span>
    </div>
  );
};

export default MessagesSkeleton;
